import type { NextPageWithLayout } from 'next';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef } from 'react';

import { Alter25Section } from '@/components/alter25';
import { Alter25Footer } from '@/components/alter25/Alter25Footer';
import { AsSeenIn } from '@/components/alter25/AsSeenIn';
import { ShopSwabKitIconPoints } from '@/components/alter25/shop/swab-kit/ShopSwabKitIconPoints';
import { ShopSwabKitPivacyGuaranteed } from '@/components/alter25/shop/swab-kit/ShopSwabKitPivacyGuaranteed';
import { ShopSwabKitQuestionsAnswered } from '@/components/alter25/shop/swab-kit/ShopSwabKitQuestionsAnswered';
import { ShopSwabKitTopSection } from '@/components/alter25/shop/swab-kit/ShopSwabKitTopSection';
import { ShopSwabKitLeadingExperts } from '@/components/alter25/shop/swab-kit/ShowSwabKitLeadingExperts';
import { YotpoReviews2 } from '@/components/alter25/yotpo/YotpoReviews2';
import Alter25Layout from '@/components/layouts/Alter25Layout';
import { ShopContext } from '@/context/ShopContext';
import { clarityEvent } from '@/lib/clarity/clarity';
import type { SwabKitProps } from '@/lib/get-static-props/shop/swab-kit';
import { getSwabKitStaticProps } from '@/lib/get-static-props/shop/swab-kit';

const DEFAULT_S1_PROMOCODE = 'ALTER25';

export const getStaticProps = getSwabKitStaticProps;

const SwabKit: NextPageWithLayout<SwabKitProps> = ({
  generalFaq,
  swabKitProduct,
  swabKitProductGallery,
}: SwabKitProps) => {
  const { cartIsAvailable, upsertToCart } = useContext(ShopContext);

  const searchParams = useSearchParams();
  const promocode = searchParams.get('promocode') || DEFAULT_S1_PROMOCODE;

  const hasUpsertedToCart = useRef(false);

  useEffect(() => {
    if (cartIsAvailable && !hasUpsertedToCart.current) {
      hasUpsertedToCart.current = true;
      // INFO: Apply discountCodes with upsert to prevent race condition.
      upsertToCart({ merchandiseId: swabKitProduct.merchandiseId, discountCodes: [promocode] });
      clarityEvent('swab_pdp');
    }
  }, [cartIsAvailable, upsertToCart, swabKitProduct.merchandiseId, promocode]);

  return (
    <>
      <Alter25Section className="md:py-10 pt-0 pb-0">
        <ShopSwabKitTopSection swabKitProductGallery={swabKitProductGallery} />
      </Alter25Section>

      <ShopSwabKitIconPoints />

      <Alter25Section className="py-16 md:py-20">
        <ShopSwabKitLeadingExperts />
      </Alter25Section>

      <AsSeenIn className="mt-14 mb-20" />

      <Alter25Section className="py-10 md:py-20">
        <ShopSwabKitPivacyGuaranteed />
      </Alter25Section>

      <YotpoReviews2 className="bg-alter25-cream-b" />

      <Alter25Section className="py-16 md:py-20">
        <ShopSwabKitQuestionsAnswered generalFaq={generalFaq} />
      </Alter25Section>

      <hr className="hidden lg:block bg-t-badge-gray " />

      <Alter25Section className="py-16 md:py-20">
        <Alter25Footer />
      </Alter25Section>
    </>
  );
};

SwabKit.getLayout = (page) => {
  return <Alter25Layout floatingBuyNow={true}>{page}</Alter25Layout>;
};

export default SwabKit;
